import { useFormContext } from "react-hook-form";
import { CotationFormData } from "@/types/forms/home/cotationFormData.form";
import { FC, useEffect, useState } from "react";
import colors from "tailwindcss/colors";
import ShipmentTransitIcon from "@/assets/icons/shipment-transit.svg?react";
import usePriceDetailCard from "./usePriceDetail";
import { formatPrice } from "@/lib/money.util";
import clsx from "clsx";
import CloseIcon from "@/assets/icons/x.svg?react";
import ChevronSvg from "@/assets/svgTsx/chevron";

interface PriceDetailLineProps {
  title?: string;
  dotColor?: string;
  value: number | string;
  textColor: string;
  isSubDetails?: boolean;
}

const HtTtcSwitch = ({ isTTC, onChange }: { isTTC: boolean; onChange: (val: boolean) => void }) => (
  <div className="flex rounded-full border border-gray-200 bg-white p-0.5 text-xs font-semibold shadow-sm">
    <div
      className={clsx(
        "cursor-pointer rounded-full px-2.5 py-0.5 transition-all",
        !isTTC ? "bg-blue-600 text-white" : "text-gray-500 hover:text-gray-700",
      )}
      onClick={() => onChange(false)}
    >
      HT
    </div>
    <div
      className={clsx(
        "cursor-pointer rounded-full px-2.5 py-0.5 transition-all",
        isTTC ? "bg-blue-600 text-white" : "text-gray-500 hover:text-gray-700",
      )}
      onClick={() => onChange(true)}
    >
      TTC
    </div>
  </div>
);

const PriceDetailLine: FC<PriceDetailLineProps> = ({
  title,
  dotColor,
  value = 0,
  textColor,
  isSubDetails,
}) => (
  <div className="flex flex-row justify-between">
    <div className="flex flex-row items-center gap-2">
      {!isSubDetails && (
        <div className="h-2 w-2 rounded-full" style={{ backgroundColor: dotColor || "#000" }} />
      )}
      <p
        style={{
          color: textColor,
          paddingLeft: isSubDetails ? 12 : 0,
        }}
        className={clsx(isSubDetails ? "text-sm" : "text-base")}
      >
        {title}
      </p>
    </div>
    <span
      className={clsx("font-semibold", { "text-sm text-gray-500": textColor === colors.gray[400] })}
      style={{ color: textColor }}
    >
      {typeof value === "number" ? formatPrice(value) : value}
    </span>
  </div>
);

const ExpressBadge = ({ label, className }: { label: string; className: string }) => (
  <div
    className={clsx(
      "items-center gap-2 rounded-3xl border border-blue-200 bg-blue-600 px-2 py-1 text-xs text-white",
      className,
    )}
  >
    <ShipmentTransitIcon />
    <span className="block text-sm font-medium">{label}</span>
  </div>
);

const PriceDetailCard = ({
  showWinePrice,
  mobileOpenState,
  setMobileOpenState,
}: {
  showWinePrice: boolean;
  mobileOpenState?: boolean;
  setMobileOpenState?: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const { watch } = useFormContext<CotationFormData>();
  const [isDetailOpen, setIsDetailOpen] = useState(true);
  const [isWoodBoxDetailOpen, setIsWoodBoxDetailOpen] = useState(false);
  const [isInsuranceDetailOpen, setIsInsuranceDetailOpen] = useState(false);
  const [isTTC, setIsTTC] = useState(true);

  const express = watch("express");
  const country = watch("country");
  const woodBoxesActivated = watch("woodBoxesActivated");

  const { priceDetail, isDestinationEU, isFrenchBillingEntity } = usePriceDetailCard();

  useEffect(() => {
    setIsTTC(true);
    setIsWoodBoxDetailOpen(false);
    setIsInsuranceDetailOpen(false);
  }, [country]);

  const transportValue = isTTC
    ? (priceDetail?.transport.transport || 0) + (priceDetail?.otherFees || 0)
    : (priceDetail?.transport.transportHt || 0) + (priceDetail?.otherFees || 0);

  const insuranceValue = isTTC
    ? priceDetail?.insurance || 0
    : priceDetail?.insuranceDetails?.insurancePriceHT || 0;

  const woodBoxesValue = isTTC
    ? priceDetail?.woodBoxes || 0
    : (priceDetail?.woodBoxDetails?.woodBoxPriceHT || 0) +
      (priceDetail?.woodBoxDetails?.membranesPriceHT || 0);

  const taxesValue = priceDetail?.taxes || 0;

  const totalPrice =
    (showWinePrice ? priceDetail?.priceWWine?.winePrice ?? 0 : 0) +
    transportValue +
    (priceDetail?.woodBoxDetails?.isOffered ? 0 : woodBoxesValue) +
    taxesValue +
    insuranceValue;

  // rounding error can cause the bar to not be full. This is a workaround
  const transportPrice = (priceDetail: any) => {
    if (!priceDetail) return 0;
    if (showWinePrice) {
      const total =
        priceDetail.priceWWine.insurancePercent +
        priceDetail.priceWWine.taxesPercent +
        priceDetail.priceWWine.transportPercent +
        priceDetail.priceWWine.winePercent +
        priceDetail.priceWWine.woodBoxesPercent +
        priceDetail.priceWWine.otherFeesPercent;
      return (
        priceDetail.priceWWine.transportPercent +
        priceDetail.priceWWine.otherFeesPercent +
        (total < 100)
      );
    }
    const total =
      priceDetail.insurancePercent +
      priceDetail.taxesPercent +
      priceDetail.transportPercent +
      priceDetail.woodBoxesPercent +
      priceDetail.otherFeesPercent;
    return priceDetail.transportPercent + priceDetail.otherFeesPercent + (total < 100);
  };

  const textDisplay = showWinePrice ? "Montant total" : "Tarif d'expédition";

  return (
    <div className="relative flex flex-1 flex-col gap-2 text-center">
      <div className="flex flex-row-reverse items-center justify-between gap-2 px-2 md:flex-row md:pt-6">
        {mobileOpenState && setMobileOpenState && (
          <div id="portal-price-detail-card-close" className="md:hidden">
            <CloseIcon className="text-gray-400" onClick={() => setMobileOpenState(false)} />
          </div>
        )}
        <div className="flex w-full items-center justify-between gap-2 px-4">
          <div className="flex flex-row items-center gap-2">
            <p className="font-medium">{textDisplay}</p>
            {express && <ExpressBadge label="Express" className="hidden md:flex" />}
          </div>
          {isDestinationEU && isFrenchBillingEntity && (
            <HtTtcSwitch isTTC={isTTC} onChange={setIsTTC} />
          )}
        </div>
        <div className="w-[24px] md:hidden" />
      </div>

      {express && (
        <div className="flex w-full items-center justify-center">
          <ExpressBadge label="Express" className="flex w-fit md:hidden" />
        </div>
      )}

      <div className="relative flex flex-1 flex-col gap-2 px-6 pb-6 text-center">
        <div className="relative flex flex-1 flex-row items-center justify-between gap-2 pb-6 text-center">
          <span className={clsx("font-jakarta-sans text-5xl font-extrabold text-blue-600")}>
            {formatPrice(totalPrice)}
          </span>
          <ChevronSvg
            width={16}
            height={16}
            color={colors.gray[400]}
            className={`${isDetailOpen ? "rotate-90" : "-rotate-90"} cursor-pointer transition-all`}
            onClick={() => setIsDetailOpen(!isDetailOpen)}
          />
        </div>
        {isDetailOpen && (
          <>
            <div className={`flex h-3 w-full flex-row overflow-hidden rounded-3xl bg-slate-50`}>
              {showWinePrice && (
                <div
                  className={`bg-rose900 h-full`}
                  style={{
                    width: `${priceDetail?.priceWWine?.winePercent || 0}%`,
                  }}
                />
              )}
              <div
                className={`bg-brand600 h-full`}
                style={{
                  width: `${transportPrice(priceDetail)}%`,
                }}
              />
              <div
                className="h-full bg-orange-400"
                style={{
                  width: `${(showWinePrice ? priceDetail?.priceWWine?.taxesPercent : priceDetail?.taxesPercent) || 0}%`,
                }}
              />
              <div
                className="h-full bg-[#964B00]"
                style={{
                  width: `${(showWinePrice ? priceDetail?.priceWWine?.woodBoxesPercent : priceDetail?.woodBoxesPercent) || 0}%`,
                }}
              />
              <div
                className="h-full bg-green-400"
                style={{
                  width: `${(showWinePrice ? priceDetail?.priceWWine?.insurancePercent : priceDetail?.insurancePercent) || 0}%`,
                }}
              />
            </div>
            <div className={`relative flex flex-1 flex-col gap-2 text-center`}>
              {showWinePrice && (
                <PriceDetailLine
                  title="Vin"
                  value={priceDetail?.priceWWine?.winePrice || 0}
                  dotColor={"#881337"}
                  textColor={colors.gray[600]}
                />
              )}
              <PriceDetailLine
                title="Transport"
                value={transportValue}
                dotColor={"#067aff"}
                textColor={colors.gray[600]}
              />
              {!watch("dapOption") && (
                <PriceDetailLine
                  title="Taxes"
                  value={taxesValue}
                  dotColor={colors.orange[400]}
                  textColor={colors.gray[600]}
                />
              )}
              <div className="flex flex-col gap-1">
                <div className="flex flex-row items-center justify-between">
                  <div className="flex flex-row items-center gap-2">
                    <div className="h-2 w-2 rounded-full" style={{ backgroundColor: "#964B00" }} />
                    <p style={{ color: colors.gray[600] }}>Option caisse bois</p>
                    {woodBoxesActivated &&
                      !priceDetail?.woodBoxDetails?.isOffered &&
                      priceDetail &&
                      !isDestinationEU && (
                        <ChevronSvg
                          width={12}
                          height={12}
                          color={colors.gray[400]}
                          className={`${isWoodBoxDetailOpen ? "rotate-90" : "-rotate-90"} cursor-pointer transition-all`}
                          onClick={() => setIsWoodBoxDetailOpen(!isWoodBoxDetailOpen)}
                        />
                      )}
                  </div>
                  <span className="font-semibold" style={{ color: colors.gray[600] }}>
                    {priceDetail?.woodBoxDetails?.isOffered
                      ? "Offert"
                      : typeof woodBoxesValue === "number"
                        ? formatPrice(woodBoxesValue)
                        : woodBoxesValue}
                  </span>
                </div>
                {isWoodBoxDetailOpen && !priceDetail?.woodBoxDetails?.isOffered && (
                  <div className="ml-4 flex flex-col gap-1">
                    {/* {isDestinationEU && (
                      <PriceDetailLine
                        title="Suppl. transport CB"
                        value={
                          isTTC
                            ? priceDetail?.woodBoxDetails?.woodBoxPrice || 0
                            : priceDetail?.woodBoxDetails?.woodBoxPriceHT || 0
                        }
                        textColor={colors.gray[400]}
                        isSubDetails={true}
                      />
                    )} */}

                    {/* {isDestinationEU && (
                      <PriceDetailLine
                        title="Protection CB"
                        value={
                          isTTC
                            ? priceDetail?.woodBoxDetails?.membranesPrice || 0
                            : priceDetail?.woodBoxDetails?.membranesPriceHT || 0
                        }
                        textColor={colors.gray[400]}
                        isSubDetails={true}
                      />
                    )} */}

                    {!isDestinationEU && (
                      <PriceDetailLine
                        value={`dont ${formatPrice(priceDetail?.woodBoxDetails?.membranesPriceTva || 0)} de TVA`}
                        textColor={colors.gray[400]}
                        isSubDetails={true}
                      />
                    )}
                  </div>
                )}
              </div>
              <div className="flex flex-col gap-1">
                <div className="flex flex-row items-center justify-between">
                  <div className="flex flex-row items-center gap-2">
                    <div
                      className="h-2 w-2 rounded-full"
                      style={{ backgroundColor: colors.green[400] }}
                    />
                    <p style={{ color: colors.gray[600] }}>Assurance</p>
                    {!isDestinationEU && priceDetail && (
                      <ChevronSvg
                        width={12}
                        height={12}
                        color={colors.gray[400]}
                        className={`${isInsuranceDetailOpen ? "rotate-90" : "-rotate-90"} cursor-pointer transition-all`}
                        onClick={() => setIsInsuranceDetailOpen(!isInsuranceDetailOpen)}
                      />
                    )}
                  </div>
                  <span className="font-semibold" style={{ color: colors.gray[600] }}>
                    {formatPrice(insuranceValue)}
                  </span>
                </div>
                {!isDestinationEU && isInsuranceDetailOpen && (
                  <div className="ml-4 flex flex-col gap-1">
                    <PriceDetailLine
                      value={`dont ${formatPrice(priceDetail?.insuranceDetails?.insurancePriceTva || 0)} de TVA`}
                      textColor={colors.gray[400]}
                      isSubDetails={true}
                    />
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PriceDetailCard;
